/**
 * Simple event emitter for cross-component communication
 * Used to notify components when threads are created/updated
 */

import { logger } from './logger';

type EventCallback = (...args: any[]) => void;

class EventEmitter {
  private events: Map<string, EventCallback[]> = new Map();

  /**
   * Subscribe to an event
   */
  on(event: string, callback: EventCallback): () => void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)!.push(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.events.get(event);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    };
  }

  /**
   * Emit an event
   */
  emit(event: string, ...args: any[]): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback(...args);
        } catch (error) {
          logger.error(`Error in event callback for ${event}`, error, 'events');
        }
      });
    }
  }

  /**
   * Remove all listeners for an event
   */
  off(event: string): void {
    this.events.delete(event);
  }

  /**
   * Remove all listeners
   */
  removeAllListeners(): void {
    this.events.clear();
  }
}

// Export singleton instance
export const eventEmitter = new EventEmitter();

// Event names
export const THREAD_EVENTS = {
  CREATED: 'thread:created',
  UPDATED: 'thread:updated',
  COMMENT_ADDED: 'thread:comment_added',
} as const;

